import { uniqBy } from 'lodash'
import { useMemo } from 'react'
import useInvestAssets from 'src/common/apollo/hooks/useInvestAssets'
import useTokens from 'src/common/apollo/hooks/useTokens'
import useTokenForOwner from 'src/common/queries/useTokenForOwner'
import { AnyToken, ERC20 } from 'src/common/types/models/Tokens'
import { isAddress } from 'thirdweb'
import { getInvestTokenFilter, getTokenFilter } from './utils'
import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'

interface UseNotOwnedTokensReturned {
  notOwnedTokens: AnyToken[]
  searchedToken: AnyToken | null
  investAssets: ERC20[]
  loading: boolean
}

export const useNotOwnedTokens = (
  debouncedSearch: string
): UseNotOwnedTokensReturned => {
  const { smartAccountInfo } = useSmartAccount()
  const isSearchByAddress = isAddress(debouncedSearch)

  const { token: searchedToken, loading: searchedTokenLoading } =
    useTokenForOwner(
      smartAccountInfo?.address,
      isSearchByAddress ? debouncedSearch : ''
    )

  const {
    erc20s,
    nfts,
    loading: tokensLoading,
  } = useTokens({
    variables: { filter: getTokenFilter(debouncedSearch) },
    skip: isSearchByAddress,
  })

  const { investAssets, loading: investAssetsLoading } = useInvestAssets({
    variables: { filter: getInvestTokenFilter(debouncedSearch) },
    skip: isSearchByAddress,
  })

  // Overall loading state
  const loading = tokensLoading || investAssetsLoading || searchedTokenLoading

  const allTokens = useMemo(() => {
    if (isSearchByAddress) return searchedToken ? [searchedToken] : []
    return uniqBy([...erc20s, ...nfts], 'id')
  }, [isSearchByAddress, searchedToken, erc20s, nfts])

  return {
    notOwnedTokens: allTokens,
    searchedToken,
    investAssets,
    loading,
  }
}
